import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Result, Row, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IUpdateMaHolderLookupListFormValues } from '../../../../../interfaces/lookup/maHolder';
import { IUpdateMaHolder } from '../../../../../interfaces/lookup/maHolder/data';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchMaHolderById, updateMaHolder } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const MaHolderLookupListEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const itemId = params.id ? parseInt(params.id) : undefined;

    const onFinish = (values: IUpdateMaHolderLookupListFormValues) => {
        const body: IUpdateMaHolder = {
            id: lookupSlice.detailsPage.maHolder.data?.id,
            ...(values as any),
        };

        dispatch(updateMaHolder(body));
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.maHolder.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/ma-holder', { replace: true });
        }
    }, [lookupSlice.detailsPage.maHolder.updateStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchMaHolderById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    useEffect(() => {
        if (lookupSlice.detailsPage.maHolder.data) {
            form.setFieldsValue({
                id: lookupSlice.detailsPage.maHolder.data.id,
                code: lookupSlice.detailsPage.maHolder.data.code,
                description: lookupSlice.detailsPage.maHolder.data.description,
            } as IUpdateMaHolderLookupListFormValues);
        }
    }, [lookupSlice.detailsPage.maHolder.data]);

    if (lookupSlice.detailsPage.maHolder.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <Form
            form={form}
            name="maholder-lookup-edit"
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={(changedValue: any, values) => {
                form.setFieldsValue(values);
            }}
        >
            <ContentHeader
                title="Manage MA Holders lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'MA Holder',
                        path: '/admin/lookup-lists/ma-holder',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.maHolder.data?.description || '-',
                        path: `/admin/lookup-lists/ma-holder/${params.id}`,
                    },
                    {
                        breadcrumbName: 'Edit',
                        path: `/admin/lookup-lists/ma-holder/${params.id}/edit`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.maHolder.fetchStatus) && (
                    <Form.Item>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => navigate(-1)}
                                disabled={
                                    lookupSlice.detailsPage.maHolder.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.maHolder.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Close
                            </Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={
                                    lookupSlice.detailsPage.maHolder.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.maHolder.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Save and close
                            </Button>
                        </Space>
                    </Form.Item>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.maHolder.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.maHolder.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.maHolder.fetchStatus} about={'MA Holder'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">MA Holder Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Code</label>
                                    <Form.Item name="code" rules={[{ required: true, message: "'Code' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Description</label>
                                    <Form.Item name="description" rules={[{ required: true, message: "'Description' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.maHolder?.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.maHolder.data?.created
                                            ? dayjs(lookupSlice.detailsPage.maHolder.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.maHolder.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.maHolder.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.maHolder.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </Form>
    );
};
